<script setup lang="ts">
    import { ref, computed } from 'vue'
    import { useVModel, computedAsync } from '@vueuse/core'
    import type { UploadedFile as VvUploadedFile } from '@volverjs/ui-vue'
    import type { Media } from 'models'
    import { Audience } from 'ability'
    import { useMediaStore } from 'composables'
    import { useI18n } from 'vue-i18n'

    const props = withDefaults(
        defineProps<{
            modelValue?: Media | Media[] | undefined
            modifiers?: string | string[]
            label?: string
            id?: string
            name: string
            accept?: string
            loading?: boolean
            valid?: boolean
            invalid?: boolean
            hint?: string
            placeholder?: string
            invalidLabel?: string | string[]
            type?: string
            multiple?: boolean
            readonly?: boolean
            max?: number | string
        }>(),
        {
            modelValue: undefined,
            id: Math.random().toString(36),
            modifiers: () => [],
            label: '',
            accept: '',
            hint: '',
            placeholder: '',
            invalidLabel: '',
            type: '',
            max: undefined,
        },
    )

    const { getDownloadHandle, createMediaFromFile } = useMediaStore()
    const { t: $t } = useI18n({
        useScope: 'global',
    })

    // file
    const isUploading = ref(false)
    const isDownloading = ref(false)
    const currentUploadIndex = ref(0)
    const currentUploadName = ref('')
    const uploadsQueueLength = ref(0)
    const progress = ref<number>(0)

    const emit = defineEmits<{
        'update:modelValue': [Media | undefined]
        'upload-success': [Media]
        'download-url': [string]
    }>()
    const localModelValue = useVModel(props, 'modelValue', emit)

    const isMultiple = computed(
        () => props.multiple || Array.isArray(localModelValue.value),
    )

    const downloadFile = async (handle: Media, audience: Audience) => {
        try {
            isDownloading.value = true
            const downloadHandle = await getDownloadHandle(handle.src, audience)
            return downloadHandle
        } catch (_e) {
            // TODO: show error
            return null
        } finally {
            isDownloading.value = false
        }
    }

    const files = computedAsync<
        VvUploadedFile<Media> | VvUploadedFile<Media>[] | undefined
    >(async () => {
        if (!localModelValue.value) {
            return []
        }
        if (Array.isArray(localModelValue.value)) {
            const toReturn: VvUploadedFile<Media>[] = []
            for (const media of localModelValue.value) {
                const downloaded = await downloadFile(
                    media,
                    Audience.Backoffice,
                )
                if (!downloaded) {
                    continue
                }
                toReturn.push({
                    name: media.name,
                    size: media.src.bytesCount,
                    type: media.src.mimeType,
                    url: downloaded.downloadHandle.downloadSasuri,
                    source: media,
                })
            }
            return toReturn
        }
        const downloaded = await downloadFile(
            localModelValue.value,
            Audience.Backoffice,
        )
        if (!downloaded) {
            return []
        }
        return {
            name: localModelValue.value.name,
            size: localModelValue.value.src.bytesCount,
            type: localModelValue.value.src.mimeType,
            url: downloaded.downloadHandle.downloadSasuri,
            source: localModelValue.value,
        }
    })

    const onUpdateModelValue = async (
        newValue: File | VvUploadedFile | (File | VvUploadedFile)[] | undefined,
    ) => {
        if (Array.isArray(newValue) && !isMultiple.value) {
            newValue = newValue?.[0]
        }
        if (!newValue) {
            if (isMultiple.value) {
                localModelValue.value = []
            } else {
                localModelValue.value = undefined
            }
            return
        }
        if (Array.isArray(newValue)) {
            uploadsQueueLength.value = newValue.length
            currentUploadIndex.value = 0
            const toReturn: Media[] = []
            for (const file of newValue) {
                currentUploadIndex.value++
                currentUploadName.value = file.name
                if (!(file instanceof File)) {
                    if (file.source) {
                        toReturn.push(file.source as Media)
                    }
                    continue
                }
                const media = await createMedia(file)
                if (media) {
                    toReturn.push(media)
                }
            }
            localModelValue.value = toReturn
            return
        }
        if (newValue instanceof File) {
            uploadsQueueLength.value = 1
            currentUploadIndex.value = 1
            currentUploadName.value = newValue.name
            const media = await createMedia(newValue)
            if (media) {
                localModelValue.value = media
            }
            return
        }
        if (newValue.source) {
            localModelValue.value = newValue.source as Media
        }
    }

    const createMedia = async (newValue: File): Promise<Media | void> => {
        isUploading.value = true
        progress.value = 0
        const media = await createMediaFromFile(newValue)
        isUploading.value = false
        progress.value = 0
        if (!media) {
            return
        }
        emit('upload-success', media)
        return media
    }

    const loadingLabel = computed(() => {
        if (isDownloading.value) {
            return $t('label.downloading')
        }
        let toReturn = ''
        if (uploadsQueueLength.value > 1) {
            toReturn =
                $t('label.upload', {
                    index: currentUploadIndex.value,
                    total: uploadsQueueLength.value,
                }) + ' - '
        }
        toReturn += `${progress.value}% ${currentUploadName.value}`
        return toReturn
    })
</script>

<template>
    <VvInputFile
        v-if="files"
        :model-value="files"
        v-bind="{
            modifiers,
            label,
            id,
            name,
            accept,
            loading: loading || isUploading || isDownloading,
            valid,
            invalid,
            invalidLabel,
            hintLabel: hint,
            loadingLabel,
            progress,
            placeholder,
            multiple,
            readonly,
            max,
        }"
        @update:model-value="onUpdateModelValue" />
</template>

<style lang="scss">
    .vv-input-file__drop-area {
        --vv-input-file-state-readonly-element-drop-area-display: block;
    }
</style>
